/**
 * TAHLEEL.ai API Service - v2.2.0
 *
 * NO FALLBACKS - NO MOCK DATA - PRODUCTION ONLY
 *
 * Flow: Upload → Preflight Check → Detect Jerseys → Select Team → Analyze Team
 *
 * NEW: preflightCheck() — Vision analysis for crop, jersey colors, team names
 * NEW: analyzeTeam() response now includes heatmap URLs + preflight data
 */

import axios from 'axios';
import { createClient } from '@supabase/supabase-js';

// ✅ Cloud Run for video analysis
const FLASK_API_URL = process.env.REACT_APP_FLASK_API_URL || 'https://tahleel-api-796940649294.us-central1.run.app';

// Supabase configuration
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://wdtejpsgexsksmtsgytu.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndkdGVqcHNnZXhza3NtdHNneXR1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM2NTk4ODAsImV4cCI6MjA2OTIzNTg4MH0.a2BXvw6rKngpvVVU0Rmdyj-mFASS4M-K4ivASBgz5IM';

// Initialize Supabase client
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Create Axios instance for Cloud Run
const flaskApi = axios.create({
  baseURL: FLASK_API_URL,
  timeout: 600000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

/**
 * ============================================
 * STEP 1: UPLOAD VIDEO TO GCS
 * ============================================
 */
export const uploadVideoToGCS = async (videoFile, onProgress) => {
  console.log('🎬 [STEP 1] Starting GCS upload...');
  console.log('📹 Video:', videoFile.name, `(${(videoFile.size / 1024 / 1024).toFixed(2)} MB)`);
  
  // Get signed upload URL from backend
  if (onProgress) onProgress(5, 'Requesting upload URL...');
  
  const urlResponse = await axios.post(
    `${FLASK_API_URL}/get-upload-url`,
    { filename: videoFile.name }
  );
  
  if (!urlResponse.data.success) {
    throw new Error('Failed to get upload URL from backend');
  }
  
  const { uploadUrl, gcsPath, videoId } = urlResponse.data;
  console.log('✅ Got signed URL for:', gcsPath);
  
  // Upload directly to Google Cloud Storage
  if (onProgress) onProgress(10, 'Uploading to cloud...');
  
  await axios.put(uploadUrl, videoFile, {
    headers: {
      'Content-Type': 'video/mp4'
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const uploadPercent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        const totalPercent = 10 + (uploadPercent * 0.8);
        onProgress(Math.round(totalPercent), `Uploading: ${uploadPercent}%`);
      }
    }
  });
  
  console.log('✅ [STEP 1] Upload complete!');
  if (onProgress) onProgress(95, 'Upload complete!');
  
  return {
    success: true,
    videoId: videoId,
    gcsPath: gcsPath,
    message: 'Video uploaded successfully'
  };
};

/**
 * ============================================
 * STEP 1.5: VISION PREFLIGHT CHECK (NEW)
 * ============================================
 * Sends sample frames to TAHLEEL AI Engine for:
 *   A) Crop/enhance instructions per broadcast style
 *   B) True jersey colors (overrides k-means)
 *   C) Team names from scoreboards/badges
 *
 * This runs automatically inside the backend pipeline,
 * but can also be called standalone for the coach to preview.
 */
export const preflightCheck = async (gcsPath, videoId) => {
  console.log('🔍 [PREFLIGHT] Running vision preflight check...');
  console.log('   GCS Path:', gcsPath);

  const response = await axios.post(
    `${FLASK_API_URL}/preflight-check`,
    {
      gcs_path: gcsPath,
      video_id: videoId || undefined,
      num_frames: 5
    },
    { timeout: 120000 }
  );

  const result = response.data;
  console.log('✅ [PREFLIGHT] Result:', result);

  if (!result.success) {
    console.warn('⚠️ Preflight failed:', result.error);
    return { success: false, error: result.error };
  }

  return {
    success: true,
    quality: result.quality,
    jerseys: result.jerseys,
    teams: result.teams,
    frameAnalysis: result.frame_analysis || null,
    videoInfo: result.video_info,
    framesAnalyzed: result.frames_analyzed,
    processingTime: result.processing_time,
  };
};

/**
 * ============================================
 * STEP 2: DETECT JERSEY COLORS
 * ============================================
 */
export const detectJerseys = async (gcsPath, videoId) => {
  console.log('🎽 [STEP 2] Detecting jersey colors...');
  console.log('   GCS Path:', gcsPath);
  console.log('   Video ID:', videoId);
  
  const response = await axios.post(
    `${FLASK_API_URL}/detect-jerseys`,
    {
      gcs_path: gcsPath,
      video_id: videoId
    },
    { timeout: 120000 }
  );
  
  const result = response.data;
  console.log('✅ [STEP 2] Jersey detection response:', result);
  
  if (!result.success) {
    // NO FALLBACK - fail explicitly
    throw new Error(result.error || result.message || 'Jersey detection failed - upload clearer footage');
  }
  
  if (!result.jerseys || result.jerseys.length < 2) {
    // NO FALLBACK - fail explicitly
    throw new Error('Could not detect 2 teams. Please upload match footage with visible players.');
  }
  
  return {
    success: true,
    jerseys: result.jerseys,
    referee: result.referee || null,
    metadata: result.metadata || {},
    message: result.message
  };
};

/**
 * ============================================
 * STEP 3: ANALYZE SELECTED TEAM
 * ============================================
 */
export const analyzeTeam = async ({ gcsPath, videoId, selectedTeamId, teamLabel, teamColor }) => {
  console.log('🎯 [STEP 3] Analyzing selected team...');
  console.log('   Team ID:', selectedTeamId);
  console.log('   Team Label:', teamLabel);
  console.log('   Team Color:', teamColor);
  
  const response = await axios.post(
    `${FLASK_API_URL}/analyze-team`,
    {
      gcs_path: gcsPath,
      video_id: videoId,
      selected_team_id: selectedTeamId,
      team_label: teamLabel,
      team_color: teamColor
    },
    { timeout: 600000 }
  );
  
  const result = response.data;
  console.log('✅ [STEP 3] Team analysis response:', result);
  
  if (!result.success) {
    // NO FALLBACK - fail explicitly
    throw new Error(result.error || 'Team analysis failed');
  }
  
  return {
    success: true,
    video_id: result.video_id,
    analyzed_team: result.analyzed_team,
    tactical_analysis: result.tactical_analysis,
    detections_summary: result.detections_summary,
    metadata: result.metadata,
    message: result.message,
    coach_message: result.coach_message,

    // Pipeline visibility — what the AI did behind the scenes
    pipeline: result.pipeline || null,

    // Heatmap signed URLs for display
    heatmaps: result.heatmaps || null,

    results: {
      tactical: result.tactical_analysis?.full_analysis || '',
      fullAnalysis: result.tactical_analysis?.full_analysis || result.message,
      formation: result.tactical_analysis?.formation_detection || '',
      strengths: result.tactical_analysis?.team_strengths || '',
      weaknesses: result.tactical_analysis?.tactical_weaknesses || '',
      recommendations: result.tactical_analysis?.counter_tactics || '',
      training: result.tactical_analysis?.training_focus || '',
      assessment: result.tactical_analysis?.overall_assessment || '',
      zoneDistribution: result.tactical_analysis?.zone_distribution || {},
      zonePercentages: result.tactical_analysis?.zone_percentages || {},
      inferredFormation: result.tactical_analysis?.inferred_formation || '',
      formationConfidence: result.tactical_analysis?.formation_confidence || '',
      tacticalIndices: result.tactical_analysis?.tactical_indices || {},
    }
  };
};

/**
 * ============================================
 * SUPABASE DATA OPERATIONS
 * ============================================
 */

export const getAnalysisHistory = async (limit = 50) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('analyses')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return {
    success: true,
    analyses: data || []
  };
};

export const getAnalysisById = async (analysisId) => {
  const { data, error } = await supabase
    .from('analyses')
    .select('*')
    .eq('id', analysisId)
    .single();

  if (error) throw error;

  return {
    success: true,
    analysis: data
  };
};

export const updateAnalysis = async (analysisId, updates) => {
  const { data, error } = await supabase
    .from('analyses')
    .update(updates)
    .eq('id', analysisId)
    .select()
    .single();

  if (error) throw error;

  return {
    success: true,
    analysis: data
  };
};

export const deleteAnalysis = async (analysisId) => {
  const { error } = await supabase
    .from('analyses')
    .delete()
    .eq('id', analysisId);

  if (error) throw error;

  return {
    success: true,
    message: 'Analysis deleted successfully'
  };
};

export const saveAnalysis = async (analysisData) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('analyses')
    .insert({
      created_by: user.id,
      ...analysisData
    })
    .select()
    .single();

  if (error) throw error;

  return {
    success: true,
    analysis: data
  };
};

/**
 * ============================================
 * HEALTH CHECK
 * ============================================
 */

export const healthCheck = async () => {
  const response = await flaskApi.get('/health');
  return response.data;
};

export const checkBackendHealth = async () => {
  const health = await healthCheck();
  return {
    online: health.status === 'ok',
    status: health.status,
    services: health,
    features: health.features || {}
  };
};

/**
 * ============================================
 * CLAUDE AI AGENT FUNCTIONS
 * ============================================
 */

export const getQuickTacticalAdvice = async (question, context = {}) => {
  console.log('💬 Tactical question:', question);
  
  const response = await axios.post(
    `${FLASK_API_URL}/api/agents/quick-advice`,
    { question, context },
    { timeout: 60000 }
  );
  
  return {
    success: true,
    response: response.data.response || response.data.advice,
    metadata: response.data.metadata || {}
  };
};

export const compareFormations = async (formation1, formation2) => {
  const response = await axios.post(
    `${FLASK_API_URL}/api/agents/formation/compare`,
    { formation1, formation2 },
    { timeout: 60000 }
  );
  
  return {
    success: true,
    response: response.data.comparison || response.data.response
  };
};

export const explainFormation = async (formation) => {
  const response = await axios.post(
    `${FLASK_API_URL}/api/agents/formation/explain`,
    { formation },
    { timeout: 60000 }
  );
  
  return {
    success: true,
    response: response.data.explanation || response.data.response
  };
};

export const generateDrill = async (objective, context = {}) => {
  const response = await axios.post(
    `${FLASK_API_URL}/api/agents/training/drill`,
    { objective, context },
    { timeout: 60000 }
  );
  
  return {
    success: true,
    response: response.data.drill || response.data.response
  };
};

export const generateTrainingPlan = async (trainingData) => {
  const response = await axios.post(
    `${FLASK_API_URL}/api/agents/training`,
    trainingData,
    { timeout: 120000 }
  );
  
  return {
    success: true,
    trainingPlan: response.data.trainingPlan || response.data.results?.training || {}
  };
};

export const agentHealthCheck = async () => {
  const cloudRunHealth = await healthCheck();
  return {
    overallStatus: cloudRunHealth.status === 'ok' ? 'healthy' : 'degraded',
    agents: {
      videoAnalysis: cloudRunHealth.status === 'ok' ? 'healthy' : 'unhealthy',
      jerseyDetection: cloudRunHealth.features?.jersey_detection ? 'healthy' : 'unknown',
      supabase: 'healthy'
    },
    timestamp: new Date().toISOString()
  };
};

/**
 * ============================================
 * AUTH UTILITIES
 * ============================================
 */

export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
};

export const logout = async () => {
  await supabase.auth.signOut();
  window.location.href = '/login';
};

export const getUserProfile = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

/**
 * ============================================
 * LEGACY FUNCTIONS - THROW ERROR IF CALLED
 * ============================================
 * These exist only to catch any code still trying to use them
 */

export const analyzeVideo = async () => {
  throw new Error('❌ analyzeVideo() is REMOVED. Use uploadVideoToGCS() → detectJerseys() → analyzeTeam() flow instead.');
};

/**
 * ============================================
 * MOVEMENT RISK ANALYSIS
 * ============================================
 */
export const analyzeMovementRisk = async ({ gcsPath, videoId, selectedTeamId, teamLabel }) => {
  console.log('🏥 [MOVEMENT RISK] Starting analysis...');

  const response = await axios.post(
    `${FLASK_API_URL}/analyze-movement-risk`,
    {
      gcs_path: gcsPath,
      video_id: videoId,
      selected_team_id: selectedTeamId || 0,
      team_label: teamLabel || 'Selected Team'
    },
    { timeout: 600000 }
  );

  const result = response.data;
  console.log('✅ [MOVEMENT RISK] Result:', result);

  return result;
};

export const runFullAnalysis = async () => {
  throw new Error('❌ runFullAnalysis() is REMOVED. Use the jersey detection flow instead.');
};

export const runTacticalAnalysis = async () => {
  throw new Error('❌ runTacticalAnalysis() is REMOVED. Use analyzeTeam() instead.');
};

export const detectFormation = async () => {
  throw new Error('❌ detectFormation() is REMOVED. Formation detection is part of analyzeTeam().');
};

export const analyzePlayerMovement = async () => {
  throw new Error('❌ analyzePlayerMovement() is REMOVED. Movement analysis is part of analyzeTeam().');
};

// Validate on startup
console.log('✅ TAHLEEL.ai API Service v2.2.0 loaded');
console.log('📡 Backend:', FLASK_API_URL);
console.log('🔍 Vision Preflight: ENABLED');
console.log('🎽 Jersey Detection Flow: ENABLED');
console.log('🔥 Heatmap Generation: ENABLED');

export default flaskApi;
